"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  RadioGroup,
  Radio,
  HStack,
  Tooltip,
  IconButton,
  FormErrorMessage,
  useToast,
  Text,
} from "@chakra-ui/react";
import { VscCallOutgoing } from "react-icons/vsc";
import { Info, Plus, Trash2 } from "lucide-react";

interface TransferCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const TransferCallModal: React.FC<TransferCallModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("transfer_call");
  const [description, setDescription] = useState(
    "Transfer the call to a human agent"
  );
  const [destinationType, setDestinationType] = useState<
    "predefined" | "inferred"
  >("predefined");
  const [transferNumber, setTransferNumber] = useState("+12134447777");
  const [transferPrompt, setTransferPrompt] = useState(
    "If the user wants to reach support, transfer to +1 (925) 222-2222; if the user wants to reach sales, transfer to +1 (925) 333-3333"
  );
  const [transferType, setTransferType] = useState<"cold" | "warm">("cold");
  const [displayedPhoneNumber, setDisplayedPhoneNumber] = useState<
    "retell" | "transferee"
  >("retell");
  const [warmTransferType, setWarmTransferType] = useState<
    "static_message" | "prompt"
  >("static_message");
  const [warmTransferMessage, setWarmTransferMessage] = useState("");
  const [warmTransferPrompt, setWarmTransferPrompt] = useState("");
  const [nameError, setNameError] = useState("");
  const [customSipHeaders, setCustomSipHeaders] = useState<
    Array<{ key: string; value: string; error?: string }>
  >([]);

  const toast = useToast();

  // Theme tokens (no custom theme checks)
  const modalBg = "white";
  const modalFg = "gray.800";
  const labelColor = "secondary.700";
  const helperMuted = "gray.600";
  const dividerColor = "gray.200";
  const inputBg = "white";
  const inputBorder = "gray.300";
  const placeholder = "gray.500";
  const iconColor = "gray.600";

  const inputProps = {
    bg: inputBg,
    borderColor: inputBorder,
    _placeholder: { color: placeholder },
    _focus: {
      borderColor: "primary.500",
      boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
    },
  } as const;

  const validateSipHeaderKey = (key: string) => {
    if (!key.startsWith("X-") && !key.startsWith("x-")) {
      return `Custom SIP header keys must start with X- for key: ${key}`;
    }
    return "";
  };

  const handleAddSipHeader = () => {
    setCustomSipHeaders([
      ...customSipHeaders,
      { key: "", value: "", error: "" },
    ]);
  };

  const handleUpdateSipHeader = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    const updated = [...customSipHeaders];
    updated[index][field] = value;
    if (field === "key") updated[index].error = validateSipHeaderKey(value);
    setCustomSipHeaders(updated);
  };

  const handleRemoveSipHeader = (index: number) => {
    setCustomSipHeaders(customSipHeaders.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!initialValues) return;

    setName(initialValues.name || "transfer_call");
    setDescription(
      initialValues.description || "Transfer the call to a human agent"
    );

    if (initialValues.transfer_destination) {
      setDestinationType(
        initialValues.transfer_destination.type || "predefined"
      );
      if (initialValues.transfer_destination.type === "predefined") {
        setTransferNumber(
          initialValues.transfer_destination.number || "+12134447777"
        );
      } else {
        setTransferPrompt(initialValues.transfer_destination.prompt || "");
      }
    }

    if (initialValues.custom_sip_headers) {
      const headers = Object.entries(initialValues.custom_sip_headers).map(
        ([key, value]) => ({
          key,
          value: value as string,
          error: validateSipHeaderKey(key),
        })
      );
      setCustomSipHeaders(headers);
    }

    if (initialValues.transfer_option) {
      if (initialValues.transfer_option.type === "cold_transfer") {
        setTransferType("cold");
        setDisplayedPhoneNumber(
          initialValues.transfer_option.show_transferee_as_caller
            ? "transferee"
            : "retell"
        );
        setWarmTransferType("static_message");
        setWarmTransferMessage("");
        setWarmTransferPrompt("");
      } else if (initialValues.transfer_option.type === "warm_transfer") {
        setTransferType("warm");
        const pho = initialValues.transfer_option.public_handoff_option;
        if (pho) {
          setWarmTransferType(pho.type || "static_message");
          if (pho.type === "static_message") {
            setWarmTransferMessage(pho.message || "");
            setWarmTransferPrompt("");
          } else {
            setWarmTransferPrompt(pho.prompt || "");
            setWarmTransferMessage("");
          }
        } else {
          setWarmTransferType("static_message");
          setWarmTransferMessage("");
          setWarmTransferPrompt("");
        }
        setDisplayedPhoneNumber("retell");
      }
    } else {
      setTransferType("cold");
      setDisplayedPhoneNumber("retell");
      setWarmTransferType("static_message");
      setWarmTransferMessage("");
      setWarmTransferPrompt("");
    }
  }, [initialValues]);

  const validateToolName = (val: string) => {
    if (!val.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (val.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(val)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const hasInvalidSipHeaders = customSipHeaders.some(
      (h) => h.key && validateSipHeaderKey(h.key)
    );
    if (hasInvalidSipHeaders) {
      toast({
        title: "Invalid SIP header keys",
        description: "All SIP header keys must start with X-",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const sipHeadersObject = customSipHeaders
      .filter((h) => h.key && h.value)
      .reduce((acc, h) => {
        acc[h.key] = h.value;
        return acc;
      }, {} as Record<string, string>);

    let transferOption: any;
    if (transferType === "cold") {
      transferOption = {
        type: "cold_transfer",
        show_transferee_as_caller: displayedPhoneNumber === "transferee",
      };
    } else {
      let publicHandoffOption: any = null;
      if (warmTransferType === "static_message") {
        publicHandoffOption = {
          type: "static_message",
          message: warmTransferMessage,
        };
      } else if (warmTransferType === "prompt") {
        publicHandoffOption = { type: "prompt", prompt: warmTransferPrompt };
      }
      transferOption = {
        type: "warm_transfer",
        public_handoff_option: publicHandoffOption,
      };
    }

    const config = {
      type: "transfer_call",
      name,
      description,
      transfer_destination: {
        type: destinationType,
        ...(destinationType === "predefined"
          ? { number: transferNumber }
          : { prompt: transferPrompt }),
      },
      transfer_option: transferOption,
      custom_sip_headers: sipHeadersObject,
    };
    onSave(config);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={modalBg} color={modalFg}>
        <ModalHeader display="flex" alignItems="center" gap={2}>
          <VscCallOutgoing size={20} />
          <span>Transfer Call</span>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color={labelColor}>Name</FormLabel>
              <Input
                id="transfer-call-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter name"
                variant="outline"
                {...inputProps}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color={labelColor}>Description (Optional)</FormLabel>
              <Textarea
                id="transfer-call-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                variant="outline"
                rows={3}
                {...inputProps}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={labelColor}>Transfer to</FormLabel>
              <RadioGroup
                value={destinationType}
                onChange={(value) =>
                  setDestinationType(value as "predefined" | "inferred")
                }
                colorScheme="primary"
              >
                <HStack spacing={6}>
                  <Radio value="predefined">Static Number</Radio>
                  <Radio value="inferred">Dynamic Routing</Radio>
                </HStack>
              </RadioGroup>

              {destinationType === "predefined" ? (
                <Input
                  id="transfer-call-static-number"
                  mt={2}
                  value={transferNumber}
                  onChange={(e) => setTransferNumber(e.target.value)}
                  placeholder="+12134447777"
                  variant="outline"
                  {...inputProps}
                />
              ) : (
                <Textarea
                  id="transfer-call-dynamic-routing"
                  mt={2}
                  value={transferPrompt}
                  onChange={(e) => setTransferPrompt(e.target.value)}
                  placeholder="Enter prompt to infer the destination number. Use {{_}} to add variables."
                  rows={4}
                  variant="outline"
                  {...inputProps}
                />
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={labelColor}>Type</FormLabel>
              <RadioGroup
                value={transferType}
                onChange={(value) => {
                  setTransferType(value as "cold" | "warm");
                  if (value === "cold") {
                    setWarmTransferType("static_message");
                    setWarmTransferMessage("");
                    setWarmTransferPrompt("");
                  } else {
                    setDisplayedPhoneNumber("retell");
                  }
                }}
                colorScheme="primary"
              >
                <HStack spacing={6}>
                  <HStack>
                    <Radio value="cold">Cold Transfer</Radio>
                    <Tooltip label="AI transfers the call to the next agent without a debrief.">
                      <IconButton
                        aria-label="Cold transfer info"
                        icon={<Info size={14} />}
                        size="xs"
                        variant="ghost"
                        color={iconColor}
                      />
                    </Tooltip>
                  </HStack>
                  <HStack>
                    <Radio value="warm">Warm Transfer</Radio>
                    <Tooltip label="AI provides a debrief to the next agent after transferring the call.">
                      <IconButton
                        aria-label="Warm transfer info"
                        icon={<Info size={14} />}
                        size="xs"
                        variant="ghost"
                        color={iconColor}
                      />
                    </Tooltip>
                  </HStack>
                </HStack>
              </RadioGroup>
            </FormControl>

            {transferType === "cold" && (
              <FormControl isRequired>
                <FormLabel color={labelColor}>Displayed Phone Number</FormLabel>
                <RadioGroup
                  value={displayedPhoneNumber}
                  onChange={(value) =>
                    setDisplayedPhoneNumber(value as "retell" | "transferee")
                  }
                  colorScheme="primary"
                >
                  <HStack spacing={6}>
                    <Radio value="retell">Agent&apos;s Number</Radio>
                    <HStack>
                      <Radio value="transferee">Transferee&apos;s Number</Radio>
                      <Tooltip label="Original Caller or Callee. If you use custom telephony, enable SIP REFER and PSTN transfer; set SIP REFER to show transferee's number.">
                        <IconButton
                          aria-label="Transferee's number info"
                          icon={<Info size={14} />}
                          size="xs"
                          variant="ghost"
                          color={iconColor}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}

            {transferType === "warm" && (
              <FormControl isRequired>
                <FormLabel color={labelColor}>Handoff Message</FormLabel>
                <RadioGroup
                  value={warmTransferType}
                  onChange={(value) =>
                    setWarmTransferType(value as "static_message" | "prompt")
                  }
                  colorScheme="primary"
                >
                  <HStack spacing={6}>
                    <Radio value="static_message">Static Sentence</Radio>
                    <Radio value="prompt">Prompt</Radio>
                  </HStack>
                </RadioGroup>

                {warmTransferType === "static_message" ? (
                  <Textarea
                    mt={2}
                    value={warmTransferMessage}
                    onChange={(e) => setWarmTransferMessage(e.target.value)}
                    placeholder="Enter static message"
                    rows={3}
                    variant="outline"
                    {...inputProps}
                  />
                ) : (
                  <Textarea
                    mt={2}
                    value={warmTransferPrompt}
                    onChange={(e) => setWarmTransferPrompt(e.target.value)}
                    placeholder="Say hello to the agent and summarize the user problem to him"
                    rows={3}
                    variant="outline"
                    {...inputProps}
                  />
                )}
              </FormControl>
            )}

            <FormControl>
              <FormLabel color={labelColor}>Custom SIP Headers</FormLabel>
              <Text fontSize="sm" color={helperMuted} mb={3}>
                Add key/value pairs for call routing, metadata, or carrier
                integration.
              </Text>

              {customSipHeaders.map((header, index) => (
                <VStack key={index} spacing={1} mb={3} align="stretch">
                  <HStack spacing={2}>
                    <FormControl isInvalid={!!header.error} flex={1}>
                      <Input
                        placeholder="Key"
                        value={header.key}
                        onChange={(e) =>
                          handleUpdateSipHeader(index, "key", e.target.value)
                        }
                        variant="outline"
                        {...inputProps}
                      />
                      {header.error && (
                        <FormErrorMessage>{header.error}</FormErrorMessage>
                      )}
                    </FormControl>
                    <Input
                      placeholder="Value"
                      value={header.value}
                      onChange={(e) =>
                        handleUpdateSipHeader(index, "value", e.target.value)
                      }
                      variant="outline"
                      flex={1}
                      {...inputProps}
                    />
                    <IconButton
                      aria-label="Remove header"
                      icon={<Trash2 size={16} />}
                      size="sm"
                      variant="ghost"
                      color={iconColor}
                      onClick={() => handleRemoveSipHeader(index)}
                    />
                  </HStack>
                </VStack>
              ))}

              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddSipHeader}
                color={helperMuted}
                _hover={{ bg: "gray.100" }}
              >
                Add
              </Button>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={dividerColor}>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="primary.500"
            color="white"
            _hover={{ bg: "primary.600" }}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
